import { IApplication } from "../../Interfaces/Application/IApplication";
import { IDealStats } from "../../Interfaces/IDealStats";
import { IAppStatus } from "../../Interfaces/Application/IAppStatus";
import { IEmployee } from "../../Interfaces/Admin/IEmployee";
import { IState } from "../../Interfaces/Common/IState";
import { ICustomField } from "../../Interfaces/Common/ICustomField";
import { IHousingStatus } from "../../Interfaces/Customer/IHousingStatus";
import { ICustomerRole } from "../../Interfaces/Customer/ICustomerRole";
import { IIncomeType } from "../../Interfaces/Common/IIncomeType";
import { IJobCode } from "../../Interfaces/Common/IJobCode";
import { IJobType } from "../../Interfaces/Common/IJobType";
import { IWorkShift } from "../../Interfaces/Common/IWorkShift";
import { IIncomePeriod } from "../../Interfaces/Common/IIncomePeriod";
import { ILoggedInUser } from "../../Interfaces/ILoggedInUser";
import { IQuotePaymentInfo } from "../../Interfaces/Common/IQuotePaymentInfo";
import { IEmployeeRole } from "../../Interfaces/Admin/IEmployeeRole";
import { IMaritalStatus } from "../../Interfaces/Common/IMaritalStatus";
import { IQuotePayType } from "../../Interfaces/Common/IQuotePayType";
import { IErrorMessage } from "../../Interfaces/UI/IErrorMessage";
import { ThunkDispatch } from 'redux-thunk';
import type { BusinessRulesEngine } from "../../Data/BusinessRulesData";
import { ApplicationBudget } from "../../Data/ApplicationBudgetData";
import { ICustomerCredit } from '../../Interfaces/Customer/ICustomerCredit';
import { IAppCustomer } from '../../Interfaces/Application/IAppCustomer';
import { IApplicationStatusWorkflow } from "../../Interfaces/Application/IApplicationStatusWorkflowItem";
import { IApplicationNotes } from '../../Interfaces/Application/IApplicationNotes';
import { IAMSDealerFlowData, IDealertrackDealerDecisionData } from '../../Data/IDealertrackDealerDecisionData';
import { IDealerServiceAppHistoryItems } from '../../Interfaces/Common/IDealerServiceAppHistory';
import { IApplicationChecklist } from "../../Interfaces/Application/IApplicationChecklist";
import { ILookupItems } from "../../Interfaces/ILookupItems";
import { IApplicationChat } from "../../Interfaces/Application/IApplicationChat";
import { IEntityCustomField } from "../../Interfaces/Application/IEntityCustomField";
import { AppState } from '../rootReducer';
import { IApplicationExceptionsCustomField } from "../../Interfaces/Application/IApplicationExceptionsCustomField";
import { IApplicationException } from "../../Interfaces/Application/IApplicationException";
import {
  ApplicationDownPaymentRequest,
  ApplicationDownPaymentResponse,
  ApplicationFinanceQuote,
  ApplicationFormConfig,
  Income,
  Organization,
  PerformanceIndicatorEvaluationResult,
  ReleaseNotes,
  DownPaymentRequestStatus,
  VehicleJdPower, Vehicle, VehicleJdPowersport
} from "@amsanalytics/react-shared-library";
import { IApplicationScore } from "../../Interfaces/Application/IApplicationScore";
import { IApplicationRehash } from "../../Interfaces/Application/IApplicationRehash";
import { IJdPowersportHistoryItems } from "../../Components/Views/OrderEditor/JdPowersportHistory";
import { CreateIncome } from "../../Data/CreateIncome";
import { IEmailNotificationItems } from "../../Components/Views/OrderEditor/EmailNotifications";
import { ICustomerReference } from "../../Interfaces/Customer/ICustomerReference";
import { IAmsPreviousLoanData } from "../../Interfaces/Common/IAmsPreviousLoanData";
import { ApplicationPerformanceIndicatorEvaluationResult } from "@amsanalytics/react-shared-library";
import { ApplicationFormResult } from "../../Components/Applications/ApplicationConfiguration/ApplicationConfiguration";
import { IApplicationQueueLoadingDetail } from "../../Interfaces/Common/IApplicationQueueLoadingDetail";
import { State } from "@progress/kendo-data-query";
import { IApplicationQueueCountsResult, IGetApplicationQueueCounts } from "../../Interfaces/Common/IApplicationQueue";
import {
  IEmployeeApplicationHistory,
} from "../../Interfaces/Common/IEmployeeApplicationHistory";
import { KeyFactorValidationResult } from "../../Data/KeyFactorThreshold";
import { ApplicationFunding } from "../../Interfaces/Application/ApplicationFunding";
import { IStipulationCategory } from "../../Interfaces/StipulationTypes";
import { GlobalSearchPage } from "../../Data/GlobalSearchPage";
import { GlobalSearchDataState } from "../../Data/GlobalSearchDataState";
import {ILosCustomerIdAndAmsCustomerNumberPair} from "../../Interfaces/Common/IPostAmsLoan";
import {CarletonCalculationResult} from "../../Interfaces/Application/CarletonCalculationResult";
import {ApplicationLeaseCalculationResult, LeaseCalcMode} from "../../Interfaces/Application/ApplicationLeaseCalculationResult";

// state for the applications store.
export interface ApplicationState {
  loadingInitialData: boolean;
  loading: boolean;
  dealStats: IDealStats;
  appStatuses: IAppStatus[];
  disabledAppStatuses: string[];
  applicationStatusWorkflows: IApplicationStatusWorkflow[];
  customerRoles: ICustomerRole[];
  employeeRoles: IEmployeeRole[];
  states: IState[];
  employees: IEmployee[];
  vehicles: Vehicle[];
  housingStatuses: IHousingStatus[];
  maritalStatuses: IMaritalStatus[];
  customFields: ICustomField[];
  editingApp?: IApplication;
  appLoadedBusinessRulesHaveRun: boolean;
  applicationChats: IApplicationChat[];
  // dealertrackDealerDecisionData?: IDealertrackDealerDecisionData | IAMSDealerFlowData;
  appDirty: boolean;
  financeQuoteIsDirty: boolean;
  applicationLeaseQuoteIsDirty: boolean; // Lease quote edited in this session (drives the Save prompt)
  applicationLeaseQuoteNeedsSolve: boolean; // Lease quote not solved for its current inputs, per the server (drives the yellow)
  leaseQuoteSolvedRollback: number | null; // Roll Back target of the last Solve the page knows of (0 for Quote / Solve); null when unknown
  incomePeriods: IIncomePeriod[];
  incomeTypes: IIncomeType[];
  jobCodes: IJobCode[];
  jobTypes: IJobType[];
  workShifts: IWorkShift[];
  loggedInUser?: ILoggedInUser;
  amsAcvPermissions?: IAmsAcvPermissions; // AMS CarSec ACV grants for the logged-in user; undefined = not yet fetched
  quotePaymentInfo?: IQuotePaymentInfo;
  quotePayTypes: IQuotePayType[];
  serverMessage: IErrorMessage;
  authenticationTimedOut?: boolean;
  ipBlocked?: boolean;
  ipBlockedMessage?: string;
  newApplicationId?: string;
  shouldOpenGeneratePortalLinkDialog: boolean;
  applicantCreditAppFormConfiguration?: ApplicationFormConfig;
  coApplicantCreditAppFormConfiguration?: ApplicationFormConfig;
  lookupsLoaded: boolean;
  lookups: ILookupItems;
  // Org-management page list. Unlike lookups.organizations (shared, StorageLot-filtered), this INCLUDES
  // StorageLot orgs so they can be edited on the Organizations page. Loaded from api/Organization/forManagement.
  managementOrganizations: Organization[];
  dealerServiceAppHistoryItems: IDealerServiceAppHistoryItems;
  applicationChecklists: IApplicationChecklist[];
  applicationQueueDetails: IApplicationQueueLoadingDetail[];
  creditPullPrimaryTransactionId?: string;
  applicationScore?: IApplicationScore;
  previousAmsLoans?: IAmsPreviousLoanData[];
  applicationDownPaymentResponses?: ApplicationDownPaymentResponse[];
  applicationRehashes: IApplicationRehash[];
  jdPowersportHistories: IJdPowersportHistoryItems[];
  incompleteAppCustomerStipulationCategories: number[];
  emailNotificationItems: IEmailNotificationItems[];
  releaseNotes: ReleaseNotes[];
  applicationPerformanceIndicatorEvaluations: ApplicationPerformanceIndicatorEvaluationResult[]; // This is for the current application
  scoreEditorPerformanceIndicatorEvaluations: PerformanceIndicatorEvaluationResult[]; // This is for the Admin Screen
  employeeApplicationHistories: IEmployeeApplicationHistory[];
  keyFactorValidationResults: KeyFactorValidationResult[]; // Results of a KeyFactorThreshold check when a validator business rule runs
  stipulationCategories: IStipulationCategory[] | null; // Stipulation categories for the application. Used in business rule that sets the stipCategory-dynamicText actions
  newlyCreatedDownPaymentRequest: ApplicationDownPaymentRequest | null; // Temporarily holds the newly created down payment request so the dialog can show the iframe
  downPaymentRequestError: string | null; // Holds the error message from a failed down payment request so the dialog can display it inline
  globalSearchText: string; // search text entered into the Global Search bar
  isGloballySearchingApps: boolean; // Used to determine if the app is in a global search state
  globalSearchPage: GlobalSearchPage; // Used to store the results of the global search
  loadingGlobalSearchPage: boolean; // whether or not the system is searching for contacts for the Global Search
  lastSolvedSelectedFinanceQuote?: ApplicationFinanceQuote; // Snapshot of the selected (last solved) finance quote. Set on app load and overwritten on each successful solve.
  signedUrls: Record<string, string>; // Maps customerCreditId → signed iframe URL, stored outside editingApp
}

// action names
export const SET_DEAL_STATS = "SET_DEAL_STATS";
export const SET_APP_STATUSES = "SET_APP_STATUSES";
export const SET_DISABLED_APP_STATUSES = "SET_DISABLED_APP_STATUSES";
export const SET_INCOMPLETE_APPCUSTOMERS_STIPULATION_CATEGORIES = "SET_INCOMPLETE_APPCUSTOMERS_STIPULATION_CATEGORIES";
export const SET_APP_STATUS_WORLFLOWS = 'SET_APP_STATUS_WORLFLOWS';
export const SET_LOCATIONS = "SET_LOCATIONS";
export const SET_EMPLOYEES = "SET_EMPLOYEES";
export const SET_STATES = "SET_STATES";
export const SET_HOUSING_STATUSES = "SET_HOUSING_STATUSES";
export const SET_MARITAL_STATUSES = "SET_MARITAL_STATUSES";
export const SET_EDITING_APP = "SET_EDITING_APP";
export const CLEAR_APPLICATION_CHATS = "CLEAR_APPLICATION_CHATS";
export const SET_SHOULD_OPEN_GENERATE_PORTAL_LINK_DIALOG = "SET_SHOULD_OPEN_GENERATE_PORTAL_LINK_DIALOG";
export const SET_VEHICLES = "SET_VEHICLES";
export const SET_CUSTOM_FIELDS = "SET_CUSTOM_FIELDS";
export const SET_CUSTOMER_ROLES = "SET_CUSTOMER_ROLES";
export const SET_EMPLOYEE_ROLES = "SET_EMPLOYEE_ROLES";

export const SET_INCOME_PERIODS = "SET_INCOME_PERIODS";
export const SET_INCOME_TYPES = "SET_INCOME_TYPES";
export const SET_JOB_CODES = "SET_JOB_CODES";
export const SET_JOB_TYPES = "SET_JOB_TYPES";
export const SET_WORK_SHIFTS = "SET_WORK_SHIFTS";
export const SET_QUOTE_PAY_TYPES = "SET_QUOTE_PAY_TYPES";

export const SET_INITIAL_LOAD_COMPLETE = "SET_INITIAL_LOAD_COMPLETE";
export const SCORE_APP = "SCORE_APP";
export const CANCEL_APP_CHANGES = "CANCEL_APP_CHANGES";
export const UPDATE_EDITING_APP = "UPDATE_EDITING_APP";
export const UPDATE_APP_LOCK = "UPDATE_APP_LOCK";
export const UPDATE_BUDGET = "UPDATE_BUDGET";

export const UPDATE_APPLICATION_QUOTE_USE_VEHICLE = "UPDATE_APPLICATION_QUOTE_USE_VEHICLE";
export const SET_LOGGED_IN_USER = "SET_LOGGED_IN_USER";
export const SET_AMS_ACV_PERMISSIONS = "SET_AMS_ACV_PERMISSIONS";
export const GET_700_CREDIT_TRANSACTION_ID = "GET_700_CREDIT_TRANSACTION_ID";
export const SET_VEHICLE_JDPOWER = "SET_VEHICLE_JDPOWER";
export const GET_APPLICATION_SCORE = "GET_APPLICATION_SCORE";
export const CLEAR_APPLICATION_SCORE = "CLEAR_APPLICATION_SCORE";
export const CLEAR_APPLICATION_REHASHES = "CLEAR_APPLICATION_REHASHES";
export const GET_APPLICATION_REHASH_SUCCESS = "GET_APPLICATION_REHASH_SUCCESS";
export const SET_QUOTE_PAYMENT_INFO = "SET_QUOTE_PAYMENT_INFO";
export const SET_LOADING = "SET_LOADING";
export const SET_APP_DIRTY = "SET_APP_DIRTY";
export const SET_SERVER_MESSAGE = "SET_SERVER_MESSAGE";
export const SET_CLEAR_SERVER_MESSAGE = "SET_CLEAR_SERVER_MESSAGE";
export const SET_AUTHENTICATION_TIMED_OUT = "SET_AUTHENTICATION_TIMED_OUT";
export const SET_IP_BLOCKED = "SET_IP_BLOCKED";

export const UPDATE_APP_VEHICLE_PRICE = "UPDATE_APP_VEHICLE_PRICE";
export const ADD_NEW_APP_VEHICLE = "ADD_NEW_APP_VEHICLE";

export const CREATE_APPLICATION_SUCCESS = "CREATE_APPLICATION_SUCCESS";
export const CREATE_APPLICATION_FROM_DEALER_FORM_SUCCESS = "CREATE_APPLICATION_FROM_DEALER_FORM_SUCCESS";
export const CREATE_CASH_DEAL_APPLICATION = "CREATE_CASH_DEAL_APPLICATION";
export const CREATE_CASH_DEAL_APPLICATION_SUCCESS = "CREATE_CASH_DEAL_APPLICATION_SUCCESS";

// Application Unlock (Granular Section-Based)
export const UNLOCK_APPLICATION_SECTIONS = "UNLOCK_APPLICATION_SECTIONS";
export const UNLOCK_APPLICATION_SECTIONS_SUCCESS = "UNLOCK_APPLICATION_SECTIONS_SUCCESS";
export const UNLOCK_APPLICATION_SECTIONS_FAILURE = "UNLOCK_APPLICATION_SECTIONS_FAILURE";
export const FETCH_ACTIVE_UNLOCK = "FETCH_ACTIVE_UNLOCK";
export const FETCH_ACTIVE_UNLOCK_SUCCESS = "FETCH_ACTIVE_UNLOCK_SUCCESS";
export const RELOCK_APPLICATION = "RELOCK_APPLICATION";
export const RELOCK_APPLICATION_SUCCESS = "RELOCK_APPLICATION_SUCCESS";
export const CLEAR_ACTIVE_UNLOCK = "CLEAR_ACTIVE_UNLOCK";

export const GET_DEALERTRACK_APPLICATION_DATA_SUCCESS = "GET_DEALERTRACK_APPLICATION_DATA_SUCCESS";
export const GET_DEALERTRACK_APPLICATION_HISTORY_SUCCESS = "GET_DEALERTRACK_APPLICATION_HISTORY_SUCCESS";
export const GET_APPLICATION_CHAT_SUCCESS = "GET_APPLICATION_CHAT_SUCCESS";

export const UPDATE_EMPLOYEE_AVAILABILITY_STATUS_SUCCESS_ACTION = 'UPDATE_EMPLOYEE_AVAILABILITY_STATUS_SUCCESS_ACTION';

export const UPDATE_CREDIT_APP_FORM_CONFIGURATIONS_SUCCESS = "UPDATE_CREDIT_APP_FORM_CONFIGURATIONS_SUCCESS";

export const UPDATE_CUSTOMER_CREDIT = "UPDATE_CUSTOMER_CREDIT";

export const ADD_CO_APPLICANT = "ADD_CO_APPLICANT";

export const SEND_TO_DEALER_TRACK = 'SEND_TO_DEALER_TRACK';
export const ADD_APPLICATION_NOTES_SUCCESS = 'ADD_APPLICATION_NOTES_SUCCESS';
export const ADD_APPLICATION_CHAT_SUCCESS = 'ADD_APPLICATION_CHAT_SUCCESS';
export const UPDATE_APPLICATION_EXCEPTIONS_SUCCESS = 'UPDATE_APPLICATION_EXCEPTIONS_SUCCESS';
export const UPDATE_UNDO_APPLICATION_EXCEPTIONS_SUCCESS = 'UPDATE_UNDO_APPLICATION_EXCEPTIONS_SUCCESS';
export const UPDATE_APPLICATION_CHECKLIST_SUCCESS = 'UPDATE_APPLICATION_CHECKLIST_SUCCESS';

export const LOAD_LIBRARY_SUCCESS = "LOAD_LIBRARY_SUCCESS";

export const SET_APPLICANT_CREDIT_APP_FORM_CONFIG = "SET_APPLICANT_CREDIT_APP_FORM_CONFIG";
export const SET_CO_APPLICANT_CREDIT_APP_FORM_CONFIG = 'SET_CO_APPLICANT_CREDIT_APP_FORM_CONFIG';
export const LOAD_APPLICATION_QUEUE_DATA = "LOAD_APPLICATION_QUEUE_DATA";
export const LOAD_APPLICATION_QUEUE_COUNTS = "LOAD_APPLICATION_QUEUE_COUNTS";
export const LOAD_APPLICATION_QUEUE_COUNTS_SUCCESS = "LOAD_APPLICATION_QUEUE_COUNTS_SUCCESS";
export const UPDATE_APPLICATION_QUEUE_FILTERS_SUCCESS = "UPDATE_APPLICATION_QUEUE_FILTERS_SUCCESS";
export const CLEAR_ALL_APPLICATION_QUEUES_FILTERS_SUCCESS = "CLEAR_ALL_APPLICATION_QUEUES_FILTERS_SUCCESS";
export const LOAD_EMPLOYEE_APPLICATION_HISTORIES = "LOAD_EMPLOYEE_APPLICATION_HISTORIES";
export const LOAD_EMPLOYEE_APPLICATION_HISTORIES_SUCCESS = "LOAD_EMPLOYEE_APPLICATION_HISTORIES_SUCCESS";

export const CREATE_EMPLOYEE_APPLICATION_HISTORY = "CREATE_EMPLOYEE_APPLICATION_HISTORY";
export const CREATE_EMPLOYEE_APPLICATION_HISTORY_SUCCESS = "CREATE_EMPLOYEE_APPLICATION_HISTORY_SUCCESS";


export const SET_VEHICLE_INFO = "SET_VEHICLE_INFO";

export const UPDATE_APPLICATION_FINANCE_QUOTES = "UPDATE_APPLICATION_FINANCE_QUOTES";
export const UPDATE_APPLICATION_FINANCE_QUOTES_SUCCESS = "UPDATE_APPLICATION_FINANCE_QUOTES_SUCCESS";

export const SOLVE_LEASE_QUOTE = "SOLVE_LEASE_QUOTE";
export const SOLVE_LEASE_QUOTE_SUCCESS = "SOLVE_LEASE_QUOTE_SUCCESS";
export const SET_LEASE_QUOTE_NEEDS_SOLVE = "SET_LEASE_QUOTE_NEEDS_SOLVE";

// Create/Update/Delete Incomes
export const CREATE_INCOME = "CREATE_INCOME";
export const CREATE_INCOME_SUCCESS = "CREATE_INCOME_SUCCESS";
export const UPDATE_INCOME = "UPDATE_INCOME";
export const UPDATE_INCOME_SUCCESS = "UPDATE_INCOME_SUCCESS";
export const DELETE_INCOME = "DELETE_INCOME";
export const DELETE_INCOME_SUCCESS = "DELETE_INCOME_SUCCESS";

// Update Vehicle
export const UPDATE_VEHICLE = "UPDATE_VEHICLE";
export const UPDATE_VEHICLE_SUCCESS = "UPDATE_VEHICLE_SUCCESS";

export const SEND_CUSTOMER_REFERENCES_TO_AMS = "SEND_CUSTOMER_REFERENCES_TO_AMS";
export const SEND_CUSTOMER_REFERENCES_TO_AMS_SUCCESS = "SEND_CUSTOMER_REFERENCES_TO_AMS_SUCCESS";

export const CREATE_ORGANIZATION = 'CREATE_ORGANIZATION';
export const CREATE_ORGANIZATION_SUCCESS = 'CREATE_ORGANIZATION_SUCCESS';
export const UPDATE_ORGANIZATION = 'UPDATE_ORGANIZATION';
export const UPDATE_ORGANIZATION_SUCCESS = 'UPDATE_ORGANIZATION_SUCCESS';
export const LOAD_MANAGEMENT_ORGANIZATIONS = 'LOAD_MANAGEMENT_ORGANIZATIONS';
export const LOAD_MANAGEMENT_ORGANIZATIONS_SUCCESS = 'LOAD_MANAGEMENT_ORGANIZATIONS_SUCCESS';

export const GET_PREVIOUS_AMS_LOANS = 'GET_PREVIOUS_AMS_LOANS';
export const GET_PREVIOUS_AMS_LOANS_SUCCESS = 'GET_PREVIOUS_AMS_LOANS_SUCCESS';
export const CLEAR_PREVIOUS_AMS_LOANS = 'CLEAR_PREVIOUS_AMS_LOANS';
export const GET_DOWN_PAYMENT_HISTORIES = 'GET_DOWN_PAYMENT_HISTORIES';
export const GET_DOWN_PAYMENT_HISTORIES_SUCCESS = 'GET_DOWN_PAYMENT_HISTORIES_SUCCESS';
export const CLEAR_DOWN_PAYMENT_HISTORIES = 'CLEAR_DOWN_PAYMENT_HISTORIES';
export const SET_APP_LOADED_BUSINESS_RULES_HAVE_RUN = 'SET_APP_LOADED_BUSINESS_RULES_HAVE_RUN';

export const LOAD_RELEASE_NOTES = 'LOAD_RELEASE_NOTES';
export const LOAD_RELEASE_NOTES_SUCCESS = 'LOAD_RELEASE_NOTES_SUCCESS';
export const LOAD_CURRENT_RELEASE_NOTES = 'LOAD_CURRENT_RELEASE_NOTES';
export const LOAD_CURRENT_RELEASE_NOTES_SUCCESS = 'LOAD_CURRENT_RELEASE_NOTES_SUCCESS';

export const CREATE_CUSTOMER_REFERENCE = 'CREATE_CUSTOMER_REFERENCE';
export const CREATE_CUSTOMER_REFERENCE_SUCCESS = 'CREATE_CUSTOMER_REFERENCE_SUCCESS';

export const UPDATE_CUSTOMER_REFERENCE = 'UPDATE_CUSTOMER_REFERENCE';
export const UPDATE_CUSTOMER_REFERENCE_SUCCESS = 'UPDATE_CUSTOMER_REFERENCE_SUCCESS';

export const DELETE_CUSTOMER_REFERENCE = 'DELETE_CUSTOMER_REFERENCE';
export const DELETE_CUSTOMER_REFERENCE_SUCCESS = 'DELETE_CUSTOMER_REFERENCE_SUCCESS';

export const UPDATE_CUSTOMER_REFERENCE_VERIFICATION = 'UPDATE_CUSTOMER_REFERENCE_VERIFICATION';
export const UPDATE_CUSTOMER_REFERENCE_VERIFICATION_SUCCESS = 'UPDATE_CUSTOMER_REFERENCE_VERIFICATION_SUCCESS';

export const CREATE_APPLICATION_DOWN_PAYMENT_REQUEST = 'CREATE_APPLICATION_DOWN_PAYMENT_REQUEST';
export const CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_SUCCESS = 'CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_SUCCESS';
export const CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_FAILURE = 'CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_FAILURE';
export const CLEAR_NEWLY_CREATED_DOWN_PAYMENT_REQUEST = 'CLEAR_NEWLY_CREATED_DOWN_PAYMENT_REQUEST';

export const UPDATE_KEY_FACTOR_VALIDATION_RESULTS = 'UPDATE_KEY_FACTOR_VALIDATION_RESULTS';

export const GET_CARLETON_APR_FOR_APPLICATION_FUNDING = "GET_CARLETON_APR_FOR_APPLICATION_FUNDING";
export const GET_CARLETON_APR_FOR_APPLICATION_FUNDING_SUCCESS = "GET_CARLETON_APR_FOR_APPLICATION_FUNDING_SUCCESS";

export const UPDATE_AMS_CUSTOMER_NUMBER_SUCCESS = 'UPDATE_AMS_CUSTOMER_NUMBER_SUCCESS';

export const GET_NEW_SIGNED_URL = 'GET_NEW_SIGNED_URL';
export const GET_NEW_SIGNED_URL_SUCCESS = 'GET_NEW_SIGNED_URL_SUCCESS';
export const GET_SIGNED_URLS = 'GET_SIGNED_URLS';
export const GET_SIGNED_URLS_SUCCESS = 'GET_SIGNED_URLS_SUCCESS';

export const VOID_DOWN_PAYMENT = 'VOID_DOWN_PAYMENT';
export const VOID_DOWN_PAYMENT_SUCCESS = 'VOID_DOWN_PAYMENT_SUCCESS';

interface UpdateAppVehicleInfoAction {
  type: typeof SET_VEHICLE_INFO;
  payload: { vehicleUpdateInfo: { make: string, model: string, year: number } };
}

interface UpdateAppVehiclePriceAction {
  type: typeof UPDATE_APP_VEHICLE_PRICE;
  payload: { vehicleUpdateInfo: { vehicleId: string, vehiclePrice: number } };
}

interface AddAppVehicleAction {
  type: typeof ADD_NEW_APP_VEHICLE;
  payload: { vehicleAddInfo: Vehicle };
}

interface SetAuthenticationTimedOutAction {
  type: typeof SET_AUTHENTICATION_TIMED_OUT;
  payload: boolean;
}

interface SetIpBlockedAction {
  type: typeof SET_IP_BLOCKED;
  payload: {
    blocked: boolean;
    message: string;
  };
}

interface SetClearServerMessageAction {
  type: typeof SET_CLEAR_SERVER_MESSAGE;
}

interface SetServerMessageAction {
  type: typeof SET_SERVER_MESSAGE;
  payload: IErrorMessage;
}

interface SetDealStatsAction {
  type: typeof SET_DEAL_STATS;
  payload: IDealStats;
}

interface SetAppStatusesAction {
  type: typeof SET_APP_STATUSES;
  payload: IAppStatus[];
}
interface SetDisabledAppStatusesAction {
  type: typeof SET_DISABLED_APP_STATUSES;
  payload: string[];
}
interface SetInvalidAppCustomersStipulationGroupsAction {
  type: typeof SET_INCOMPLETE_APPCUSTOMERS_STIPULATION_CATEGORIES;
  payload: number[];
}
interface SetApplicationWorkflowsAction {
  type: typeof SET_APP_STATUS_WORLFLOWS;
  payload: IApplicationStatusWorkflow[];
}
interface SetCustomerRolesAction {
  type: typeof SET_CUSTOMER_ROLES;
  payload: ICustomerRole[];
}

interface SetEmployeeRolesAction {
  type: typeof SET_EMPLOYEE_ROLES;
  payload: IEmployeeRole[];
}

interface SetHousingStatusesAction {
  type: typeof SET_HOUSING_STATUSES;
  payload: IHousingStatus[];
}

interface SetMaritalStatusesAction {
  type: typeof SET_MARITAL_STATUSES;
  payload: IMaritalStatus[];
}

// interface SetNoteTypesAction {
//   type: typeof SET_NOTE_TYPES;
//   payload: INoteType[];
// }

interface SetSaleLocationsAction {
  type: typeof SET_LOCATIONS;
  payload: Organization[];
}
//interface SetPreferredLocationAction {
//  type: typeof SET_PREFERRED_LOCATION;
//  payload: Organization;
//}

interface SetStatesAction {
  type: typeof SET_STATES;
  payload: IState[];
}

interface SetEmployeesAction {
  type: typeof SET_EMPLOYEES;
  payload: IEmployee[];
}

interface SetEditingAction {
  type: typeof SET_EDITING_APP;
  payload?: IApplication;
}

interface ClearApplicationChatsAction {
  type: typeof CLEAR_APPLICATION_CHATS;
}

interface SetIncomePeriodsAction {
  type: typeof SET_INCOME_PERIODS;
  payload: IIncomePeriod[];
}

interface SetIncomeTypesAction {
  type: typeof SET_INCOME_TYPES;
  payload: IIncomeType[];
}

interface SetJobTypesAction {
  type: typeof SET_JOB_TYPES;
  payload: IJobType[];
}

interface SetJobCodesAction {
  type: typeof SET_JOB_CODES;
  payload: IJobCode[];
}

interface SetWorkShiftsAction {
  type: typeof SET_WORK_SHIFTS;
  payload: IWorkShift[];
}

// interface SaveEditingAction {
//   type: typeof SAVE_EDITING_APP;
// }

interface SetVehiclesAction {
  type: typeof SET_VEHICLES;
  payload: Vehicle[];
}

interface UpdateEditingAppAction {
  type: typeof UPDATE_EDITING_APP;
  payload: IApplication;
}

interface UpdateApplicationLock {
  type: typeof UPDATE_APP_LOCK;
  payload: boolean;
}

// Application Unlock (Granular Section-Based)
export interface UnlockApplicationSectionsAction {
  type: typeof UNLOCK_APPLICATION_SECTIONS;
  payload: { applicationId: string; sections: string[]; reason?: string };
}

export interface UnlockApplicationSectionsSuccessAction {
  type: typeof UNLOCK_APPLICATION_SECTIONS_SUCCESS;
  payload: any; // IApplicationUnlockLog
}

export interface UnlockApplicationSectionsFailureAction {
  type: typeof UNLOCK_APPLICATION_SECTIONS_FAILURE;
  payload: string;
}

export interface FetchActiveUnlockAction {
  type: typeof FETCH_ACTIVE_UNLOCK;
  payload: { applicationId: string };
}

export interface FetchActiveUnlockSuccessAction {
  type: typeof FETCH_ACTIVE_UNLOCK_SUCCESS;
  payload: any | null; // IApplicationUnlockLog | null
}

export interface RelockApplicationAction {
  type: typeof RELOCK_APPLICATION;
  payload: { applicationId: string };
}

export interface RelockApplicationSuccessAction {
  type: typeof RELOCK_APPLICATION_SUCCESS;
}

export interface ClearActiveUnlockAction {
  type: typeof CLEAR_ACTIVE_UNLOCK;
}

interface UpdateApplicationBudget {
  type: typeof UPDATE_BUDGET;
  payload: ApplicationBudget;
}

interface UpdateApplicationQuoteUseVehicle {
  type: typeof UPDATE_APPLICATION_QUOTE_USE_VEHICLE;
  payload: { applicationQuoteId: string, useVehiclePrice: boolean };
}

interface ScoreAppAction {
  type: typeof SCORE_APP;
}

interface SetCustomFieldsAction {
  type: typeof SET_CUSTOM_FIELDS;
  payload: ICustomField[];
}

interface SetQuotePayTypesAction {
  type: typeof SET_QUOTE_PAY_TYPES;
  payload: IQuotePayType[];
}

interface SetInitialLoadCompleteAction {
  type: typeof SET_INITIAL_LOAD_COMPLETE;
}

interface SetLoadingAction {
  type: typeof SET_LOADING;
  payload: boolean;
}

interface SetAppDirtyAction {
  type: typeof SET_APP_DIRTY;
  payload: boolean;
}

interface CancelAppChangesAction {
  type: typeof CANCEL_APP_CHANGES;
}

interface SetLoggedInUserAction {
  type: typeof SET_LOGGED_IN_USER;
  payload?: ILoggedInUser;
}

export interface IAmsAcvPermissions {
  canSeeAcv: boolean;
  canUpdateAcv: boolean;
}

interface SetAmsAcvPermissionsAction {
  type: typeof SET_AMS_ACV_PERMISSIONS;
  payload: IAmsAcvPermissions;
}

interface SetQuotePaymentInfo {
  type: typeof SET_QUOTE_PAYMENT_INFO;
  payload: IQuotePaymentInfo;
}

interface CreateNewApplicationAction {
  type: typeof CREATE_APPLICATION_SUCCESS;
  payload: { applicationId: string | undefined };
}

interface CreateApplicationFromDealerFormSuccessAction {
  type: typeof CREATE_APPLICATION_FROM_DEALER_FORM_SUCCESS;
  payload: { applicationId: string };
}

export interface CreateCashDealApplicationPayload {
  customer: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    stateAbbreviation: string;
    zipCode: string;
    email: string;
    phone: string;
  };
  organizationId: string;
  contactOpportunityId?: string;
  ownerEmployeeId?: string;
}

export interface CreateCashDealApplicationAction {
  type: typeof CREATE_CASH_DEAL_APPLICATION;
  payload: CreateCashDealApplicationPayload;
}

export const createCashDealApplication = (payload: CreateCashDealApplicationPayload): CreateCashDealApplicationAction => ({
  type: CREATE_CASH_DEAL_APPLICATION,
  payload
});

export interface CreateCashDealApplicationSuccessAction {
  type: typeof CREATE_CASH_DEAL_APPLICATION_SUCCESS;
  payload: { applicationId: string };
}

export const createCashDealApplicationSuccess = (applicationId: string): CreateCashDealApplicationSuccessAction => ({
  type: CREATE_CASH_DEAL_APPLICATION_SUCCESS,
  payload: { applicationId }
});

interface UpdateFormConfigurationAction {
  type: typeof UPDATE_CREDIT_APP_FORM_CONFIGURATIONS_SUCCESS;
  payload: { creditAppFormConfigurationDetails: ApplicationFormResult };
}

interface GetDealerTrackApplicationDataSuccessAction {
  type: typeof GET_DEALERTRACK_APPLICATION_DATA_SUCCESS;
  payload: { dealertrackDealerDecisionData: IDealertrackDealerDecisionData | IAMSDealerFlowData };
}

export const SET_DEALER_REQUEST_TRANSACTIONTYPE = 'SET_DEALER_REQUEST_TRANSACTIONTYPE';
interface SetDealerFlowTransactionTypeAction {
  type: typeof SET_DEALER_REQUEST_TRANSACTIONTYPE;
  payload: { transactionType: string };
}

interface GetDealerTrackApplicationHistorySuccessAction {
  type: typeof GET_DEALERTRACK_APPLICATION_HISTORY_SUCCESS;
  payload: { dealerServiceAppHistoryItems: IDealerServiceAppHistoryItems };
}

interface GetApplicationChatSuccessAction {
  type: typeof GET_APPLICATION_CHAT_SUCCESS;
  payload: { applicationChats: IApplicationChat[] };
}

interface UpdateEmployeeAvailabilityStatusSuccessAction {
  type: typeof UPDATE_EMPLOYEE_AVAILABILITY_STATUS_SUCCESS_ACTION;
  payload: { statusId: string };
}

interface UpdateCreditAction {
  type: typeof UPDATE_CUSTOMER_CREDIT;
  payload: { customerCredit: ICustomerCredit };
}

interface AddCoApplicantAction {
  type: typeof ADD_CO_APPLICANT;
  payload: IAppCustomer;
}

interface AddApplicationNotesSuccessAction {
  type: typeof ADD_APPLICATION_NOTES_SUCCESS;
  payload: { notes: IApplicationNotes[] };
}

interface AddApplicationChatSuccessAction {
  type: typeof ADD_APPLICATION_CHAT_SUCCESS;
  payload: { applicationChat: IApplicationChat };
}

interface UpdateApplicationExceptionsSuccessAction {
  type: typeof UPDATE_APPLICATION_EXCEPTIONS_SUCCESS;
  payload: { applicationExceptionsCustomField: IApplicationExceptionsCustomField };
}

interface UndoApplicationExceptionsSuccessAction {
  type: typeof UPDATE_UNDO_APPLICATION_EXCEPTIONS_SUCCESS;
  payload: { applicationExceptions: IApplicationException[] };
}

interface UpdateApplicationChecklistSuccessAction {
  type: typeof UPDATE_APPLICATION_CHECKLIST_SUCCESS;
  payload: { checklist: IApplicationChecklist };
}

interface LoadLibrarySuccessAction {
  type: typeof LOAD_LIBRARY_SUCCESS;
  payload: ILookupItems;
}

interface SetApplicantCreditAppFormConfig {
  type: typeof SET_APPLICANT_CREDIT_APP_FORM_CONFIG;
  payload: { applicantCreditAppFormConfig: string };
}
interface SetCoApplicantCreditAppFormConfig {
  type: typeof SET_CO_APPLICANT_CREDIT_APP_FORM_CONFIG;
  payload: { coApplicantCreditAppFormConfig: string };
}
interface LoadApplicationQueueData {
  type: typeof LOAD_APPLICATION_QUEUE_DATA;
  payload: { applicationQueueId: string, count: number, loading: boolean }
}

interface Get700CreditTransactionIdSuccessAction {
  type: typeof GET_700_CREDIT_TRANSACTION_ID;
  payload: { creditPullPrimaryTransactionId: string };
}

interface GetVehicleJdPower {
  type: typeof SET_VEHICLE_JDPOWER;
  payload: { vehicleJdPower: VehicleJdPower, isIndirectApplication: boolean };
}

interface GetApplicationScoreSuccessAction {
  type: typeof GET_APPLICATION_SCORE;
  payload: { applicationScore: IApplicationScore };
}

interface ClearApplicationScore {
  type: typeof CLEAR_APPLICATION_SCORE;
}

interface ClearApplicationRehashes {
  type: typeof CLEAR_APPLICATION_REHASHES;
}

interface GetApplicationRehashSuccessAction {
  type: typeof GET_APPLICATION_REHASH_SUCCESS;
  payload: { applicationRehashes: IApplicationRehash[] };
}

export interface UpdateFinanceQuotesAction {
  type: typeof UPDATE_APPLICATION_FINANCE_QUOTES;
  payload: { application: IApplication, newVehicle: Vehicle | undefined };
}
export const updateFinanceQuotes = (application: IApplication, newVehicle: Vehicle | undefined): UpdateFinanceQuotesAction => {
  return {
    type: UPDATE_APPLICATION_FINANCE_QUOTES,
    payload: { application, newVehicle }
  };
}

export interface UpdateFinanceQuotesSuccessAction {
  type: typeof UPDATE_APPLICATION_FINANCE_QUOTES_SUCCESS;
  payload: { carletonCalculationResult: CarletonCalculationResult, newVehicle: Vehicle | undefined };
}
export const updateFinanceQuotesSuccess = (carletonCalculationResult: CarletonCalculationResult, newVehicle: Vehicle | undefined): UpdateFinanceQuotesSuccessAction => {
  return {
    type: UPDATE_APPLICATION_FINANCE_QUOTES_SUCCESS,
    payload: { carletonCalculationResult, newVehicle }
  };
}

export interface SolveLeaseQuoteAction {
  type: typeof SOLVE_LEASE_QUOTE;
  payload: { application: IApplication, mode: LeaseCalcMode, businessRulesEngine: BusinessRulesEngine, newVehicle: Vehicle | undefined };
}
export const solveLeaseQuote = (application: IApplication, mode: LeaseCalcMode, businessRulesEngine: BusinessRulesEngine, newVehicle?: Vehicle): SolveLeaseQuoteAction => ({
  type: SOLVE_LEASE_QUOTE,
  payload: { application, mode, businessRulesEngine, newVehicle }
});

export interface SolveLeaseQuoteSuccessAction {
  type: typeof SOLVE_LEASE_QUOTE_SUCCESS;
  payload: { result: ApplicationLeaseCalculationResult, newVehicle: Vehicle | undefined };
}
export const solveLeaseQuoteSuccess = (result: ApplicationLeaseCalculationResult, newVehicle?: Vehicle): SolveLeaseQuoteSuccessAction => ({
  type: SOLVE_LEASE_QUOTE_SUCCESS,
  payload: { result, newVehicle }
});

export interface SetLeaseQuoteNeedsSolveAction {
  type: typeof SET_LEASE_QUOTE_NEEDS_SOLVE;
}
export const setLeaseQuoteNeedsSolve = (): SetLeaseQuoteNeedsSolveAction => ({ type: SET_LEASE_QUOTE_NEEDS_SOLVE });

export const LOAD_JDPOWERSPORT_HISTORY = "LOAD_JDPOWERSPORT_HISTORY";
export const LOAD_JDPOWERSPORT_HISTORY_SUCCESS = "LOAD_JDPOWERSPORT_HISTORY_SUCCESS";

export interface LoadJdPowersportHistoryAction {
  type: typeof LOAD_JDPOWERSPORT_HISTORY;
  payload: { applicationId: string }
}
export const loadJdPowersportHistory = (applicationId: string): LoadJdPowersportHistoryAction => {
  return {
    type: LOAD_JDPOWERSPORT_HISTORY,
    payload: { applicationId }
  };
}

export interface LoadJdPowersportHistoryActionSuccess {
  type: typeof LOAD_JDPOWERSPORT_HISTORY_SUCCESS;
  payload: { histories: IJdPowersportHistoryItems[] };
}
export const loadJdPowersportHistorySuccess = (histories: IJdPowersportHistoryItems[]): LoadJdPowersportHistoryActionSuccess => {
  return {
    type: LOAD_JDPOWERSPORT_HISTORY_SUCCESS,
    payload: { histories }
  };
}

export const LOAD_EMAIL_NOTIFICATIONS = "LOAD_EMAIL_NOTIFICATIONS";
export const LOAD_EMAIL_NOTIFICATIONS_SUCCESS = "LOAD_EMAIL_NOTIFICATIONS_SUCCESS";

export interface LoadEmailNotificationsAction {
  type: typeof LOAD_EMAIL_NOTIFICATIONS;
  payload: { applicationId: string }
}
export const loadEmailNotifications = (applicationId: string): LoadEmailNotificationsAction => {
  return {
    type: LOAD_EMAIL_NOTIFICATIONS,
    payload: { applicationId }
  };
}

export interface LoadEmailNotificationsActionSuccess {
  type: typeof LOAD_EMAIL_NOTIFICATIONS_SUCCESS;
  payload: { emailNotificationItems: IEmailNotificationItems[] };
}
export const loadEmailNotificationsSuccess = (emailNotificationItems: IEmailNotificationItems[]): LoadEmailNotificationsActionSuccess => {
  return {
    type: LOAD_EMAIL_NOTIFICATIONS_SUCCESS,
    payload: { emailNotificationItems }
  };
}

// Create Income
export interface CreateIncomeAction {
  type: typeof CREATE_INCOME;
  payload: { income: CreateIncome };
}

export const createIncome = (income: CreateIncome): CreateIncomeAction => {
  return {
    type: CREATE_INCOME,
    payload: { income }
  };
};

export interface CreateIncomeActionSuccess {
  type: typeof CREATE_INCOME_SUCCESS;
  payload: { income: Income, ownerId: string };
}

export const createIncomeSuccess = (income: Income, ownerId: string): CreateIncomeActionSuccess => {
  return {
    type: CREATE_INCOME_SUCCESS,
    payload: { income, ownerId }
  };
};

// Update Income
export interface UpdateIncomeAction {
  type: typeof UPDATE_INCOME;
  payload: { income: Income, ownerId: string };
}

export const updateIncome = (income: Income, ownerId: string): UpdateIncomeAction => {
  return {
    type: UPDATE_INCOME,
    payload: { income, ownerId }
  };
};

export interface UpdateIncomeActionSuccess {
  type: typeof UPDATE_INCOME_SUCCESS;
  payload: { income: Income, ownerId: string };
}

export const updateIncomeSuccess = (income: Income, ownerId: string): UpdateIncomeActionSuccess => {
  return {
    type: UPDATE_INCOME_SUCCESS,
    payload: { income, ownerId }
  };
};

// Delete Income
export interface DeleteIncomeAction {
  type: typeof DELETE_INCOME;
  payload: { incomeId: string, ownerId: string };
}

export const deleteIncome = (incomeId: string, ownerId: string): DeleteIncomeAction => {
  return {
    type: DELETE_INCOME,
    payload: { incomeId, ownerId }
  };
};

export interface DeleteIncomeActionSuccess {
  type: typeof DELETE_INCOME_SUCCESS;
  payload: { incomeId: string, ownerId: string };
}

export const deleteIncomeSuccess = (incomeId: string, ownerId: string): DeleteIncomeActionSuccess => {
  return {
    type: DELETE_INCOME_SUCCESS,
    payload: { incomeId, ownerId }
  };
};

export interface UpdateVehicleAction {
  type: typeof UPDATE_VEHICLE;
  payload: { vehicle: Vehicle };
}

export const updateVehicleAction = (vehicle: Vehicle): UpdateVehicleAction => {
  return {
    type: UPDATE_VEHICLE,
    payload: { vehicle }
  };
};

export interface UpdateVehicleActionSuccess {
  type: typeof UPDATE_VEHICLE_SUCCESS;
  payload: { vehicle: Vehicle, isIndirectApplication: boolean, newAppStatusId?: string };
}

export const updateVehicleSuccessAction = (vehicle: Vehicle, isIndirectApplication: boolean, newAppStatusId?: string): UpdateVehicleActionSuccess => {
  return {
    type: UPDATE_VEHICLE_SUCCESS,
    payload: { vehicle, isIndirectApplication, newAppStatusId }
  };
};

export interface SendCustomerReferencesToAmsAction {
  type: typeof SEND_CUSTOMER_REFERENCES_TO_AMS;
  payload: { customerReferences: ICustomerReference[] };
}

export const sendCustomerReferencesToAms = (customerReferences: ICustomerReference[]): SendCustomerReferencesToAmsAction => {
  return {
    type: SEND_CUSTOMER_REFERENCES_TO_AMS,
    payload: { customerReferences }
  };
};

export interface SendCustomerReferencesToAmsActionSuccess {
  type: typeof SEND_CUSTOMER_REFERENCES_TO_AMS_SUCCESS;
  payload: { customerReferences: ICustomerReference[] };
}

export const sendCustomerReferencesToAmsSuccess = (customerReferences: ICustomerReference[]): SendCustomerReferencesToAmsActionSuccess => {
  return {
    type: SEND_CUSTOMER_REFERENCES_TO_AMS_SUCCESS,
    payload: { customerReferences }
  };
};

/** Start Create/Edit Organizations */
export interface CreateOrganizationAction {
  type: typeof CREATE_ORGANIZATION;
  payload: Organization;
}
export const createOrganization = (organization: Organization): CreateOrganizationAction => {
  return {
    type: CREATE_ORGANIZATION,
    payload: organization
  };
};

export interface CreateOrganizationSuccessAction {
  type: typeof CREATE_ORGANIZATION_SUCCESS;
  payload: Organization;
}
export const createOrganizationSuccess = (organization: Organization): CreateOrganizationSuccessAction => {
  return {
    type: CREATE_ORGANIZATION_SUCCESS,
    payload: organization
  };
};

export interface UpdateOrganizationAction {
  type: typeof UPDATE_ORGANIZATION;
  payload: Organization;
}
export const updateOrganization = (organization: Organization): UpdateOrganizationAction => {
  return {
    type: UPDATE_ORGANIZATION,
    payload: organization
  };
};

export interface UpdateOrganizationSuccessAction {
  type: typeof UPDATE_ORGANIZATION_SUCCESS;
  payload: Organization;
}
export const updateOrganizationSuccess = (organization: Organization): UpdateOrganizationSuccessAction => {
  return {
    type: UPDATE_ORGANIZATION_SUCCESS,
    payload: organization
  };
};

/** Org-management page list (includes StorageLot) */
export interface LoadManagementOrganizationsAction {
  type: typeof LOAD_MANAGEMENT_ORGANIZATIONS;
}
export const loadManagementOrganizations = (): LoadManagementOrganizationsAction => {
  return {
    type: LOAD_MANAGEMENT_ORGANIZATIONS
  };
};

export interface LoadManagementOrganizationsSuccessAction {
  type: typeof LOAD_MANAGEMENT_ORGANIZATIONS_SUCCESS;
  payload: Organization[];
}
export const loadManagementOrganizationsSuccess = (organizations: Organization[]): LoadManagementOrganizationsSuccessAction => {
  return {
    type: LOAD_MANAGEMENT_ORGANIZATIONS_SUCCESS,
    payload: organizations
  };
};

/** END Add/Edit Organizations */

export interface GetPreviousAmsLoansAction {
  type: typeof GET_PREVIOUS_AMS_LOANS;
  payload: { applicationId: string };
}
export const getPreviousAmsLoans = (applicationId: string): GetPreviousAmsLoansAction => {
  return {
    type: GET_PREVIOUS_AMS_LOANS,
    payload: { applicationId }
  };
};

export interface GetPreviousAmsLoansSuccessAction {
  type: typeof GET_PREVIOUS_AMS_LOANS_SUCCESS;
  payload: IAmsPreviousLoanData[];
}
export const getPreviousAmsLoansSuccess = (amsPreviousLoans: IAmsPreviousLoanData[]): GetPreviousAmsLoansSuccessAction => {
  return {
    type: GET_PREVIOUS_AMS_LOANS_SUCCESS,
    payload: amsPreviousLoans
  };
};

interface ClearPreviousAmsLoansAction {
  type: typeof CLEAR_PREVIOUS_AMS_LOANS;
}

export interface GetDownPaymentHistoriesAction {
  type: typeof GET_DOWN_PAYMENT_HISTORIES;
  payload: { applicationId: string };
}
export const getDownPaymentHistories = (applicationId: string): GetDownPaymentHistoriesAction => {
  return {
    type: GET_DOWN_PAYMENT_HISTORIES,
    payload: { applicationId }
  };
};

export interface GetDownPaymentHistoriesSuccessAction {
  type: typeof GET_DOWN_PAYMENT_HISTORIES_SUCCESS;
  payload: ApplicationDownPaymentResponse[];
}
export const getDownPaymentHistoriesSuccess = (applicationDownPaymentResponses: ApplicationDownPaymentResponse[]): GetDownPaymentHistoriesSuccessAction => {
  return {
    type: GET_DOWN_PAYMENT_HISTORIES_SUCCESS,
    payload: applicationDownPaymentResponses
  };
};

interface ClearDownPaymentHistories {
  type: typeof CLEAR_DOWN_PAYMENT_HISTORIES;
}

export interface SetAppLoadedBusinessRulesHaveRunAction {
  type: typeof SET_APP_LOADED_BUSINESS_RULES_HAVE_RUN;
}

export const setAppLoadedBusinessRulesHaveRun = (): SetAppLoadedBusinessRulesHaveRunAction => {
  return {
    type: SET_APP_LOADED_BUSINESS_RULES_HAVE_RUN
  };
};

export interface SetShouldOpenGeneratePortalLinkDialogAction {
  type: typeof SET_SHOULD_OPEN_GENERATE_PORTAL_LINK_DIALOG;
  payload: boolean;
}

export const setShouldOpenGeneratePortalLinkDialog = (shouldOpenGeneratePortalLinkDialog: boolean): SetShouldOpenGeneratePortalLinkDialogAction => {
  return {
    type: SET_SHOULD_OPEN_GENERATE_PORTAL_LINK_DIALOG,
    payload: shouldOpenGeneratePortalLinkDialog
  };
};



export interface LoadReleaseNotesAction {
  type: typeof LOAD_RELEASE_NOTES;
}
export const loadReleaseNotes = (): LoadReleaseNotesAction => {
  return {
    type: LOAD_RELEASE_NOTES
  };
};

export interface LoadReleaseNotesSuccessAction {
  type: typeof LOAD_RELEASE_NOTES_SUCCESS;
  payload: { releaseNotes: ReleaseNotes[] };
}

export const loadReleaseNotesSuccess = (releaseNotes: ReleaseNotes[]): LoadReleaseNotesSuccessAction => {
  return {
    type: LOAD_RELEASE_NOTES_SUCCESS,
    payload: { releaseNotes }
  };
};

export interface LoadCurrentReleaseNotesAction {
  type: typeof LOAD_CURRENT_RELEASE_NOTES;
}

export const loadCurrentReleaseNotes = (): LoadCurrentReleaseNotesAction => {
  return {
    type: LOAD_CURRENT_RELEASE_NOTES
  };
};

export interface LoadCurrentReleaseNotesSuccessAction {
  type: typeof LOAD_CURRENT_RELEASE_NOTES_SUCCESS;
  payload: { releaseNotes: ReleaseNotes[] };
}

export const loadCurrentReleaseNotesSuccess = (releaseNotes: ReleaseNotes[]): LoadCurrentReleaseNotesSuccessAction => {
  return {
    type: LOAD_CURRENT_RELEASE_NOTES_SUCCESS,
    payload: { releaseNotes }
  };
};

// Customer References
export interface CreateCustomerReferenceAction {
  type: typeof CREATE_CUSTOMER_REFERENCE;
  payload: { customerId: string, customerReference: ICustomerReference, createdByName: string }
}

export const createCustomerReference = (customerId: string, customerReference: ICustomerReference, createdByName: string): CreateCustomerReferenceAction => {
  return {
    type: CREATE_CUSTOMER_REFERENCE,
    payload: { customerId: customerId, customerReference, createdByName }
  }
}

export interface CreateCustomerReferenceSuccessAction {
  type: typeof CREATE_CUSTOMER_REFERENCE_SUCCESS;
  payload: { customerId: string, customerReference: ICustomerReference }
}

export const createCustomerReferenceSuccess = (appCustomerId: string, customerReference: ICustomerReference): CreateCustomerReferenceSuccessAction => {
  return {
    type: CREATE_CUSTOMER_REFERENCE_SUCCESS,
    payload: { customerId: appCustomerId, customerReference }
  }
}

export interface UpdateCustomerReferenceAction {
  type: typeof UPDATE_CUSTOMER_REFERENCE;
  payload: { customerReference: ICustomerReference }
}

export const updateCustomerReference = (customerReference: ICustomerReference): UpdateCustomerReferenceAction => {
  return {
    type: UPDATE_CUSTOMER_REFERENCE,
    payload: { customerReference }
  }
}

export interface UpdateCustomerReferenceSuccessAction {
  type: typeof UPDATE_CUSTOMER_REFERENCE_SUCCESS;
  payload: { customerReference: ICustomerReference }
}

export const updateCustomerReferenceSuccess = (customerReference: ICustomerReference): UpdateCustomerReferenceSuccessAction => {
  return {
    type: UPDATE_CUSTOMER_REFERENCE_SUCCESS,
    payload: { customerReference }
  }
}

export interface DeleteCustomerReferenceAction {
  type: typeof DELETE_CUSTOMER_REFERENCE;
  payload: { customerReferenceId: string }
}

export const deleteCustomerReference = (customerReferenceId: string): DeleteCustomerReferenceAction => {
  return {
    type: DELETE_CUSTOMER_REFERENCE,
    payload: { customerReferenceId }
  }
}

export interface DeleteCustomerReferenceSuccessAction {
  type: typeof DELETE_CUSTOMER_REFERENCE_SUCCESS;
  payload: { customerReferenceId: string }
}

export const deleteCustomerReferenceSuccess = (customerReferenceId: string): DeleteCustomerReferenceSuccessAction => {
  return {
    type: DELETE_CUSTOMER_REFERENCE_SUCCESS,
    payload: { customerReferenceId }
  }
}

export interface UpdateCustomerReferenceVerification {
  type: typeof UPDATE_CUSTOMER_REFERENCE_VERIFICATION;
  payload: { customerReferenceId: string, isVerified: boolean, modifiedByName: string };
}

export const updateCustomerReferenceVerification = (customerReferenceId: string, isVerified: boolean, modifiedByName: string): UpdateCustomerReferenceVerification => {
  return {
    type: UPDATE_CUSTOMER_REFERENCE_VERIFICATION,
    payload: { customerReferenceId, isVerified, modifiedByName }
  };
}

export interface UpdateCustomerReferenceVerificationSuccessAction {
  type: typeof UPDATE_CUSTOMER_REFERENCE_VERIFICATION_SUCCESS;
  payload: { customerReferenceId: string, isVerified: boolean, modifiedByName: string };
}

export const updateCustomerReferenceVerificationSuccess = (customerReferenceId: string, isVerified: boolean, modifiedByName: string): UpdateCustomerReferenceVerificationSuccessAction => {
  return {
    type: UPDATE_CUSTOMER_REFERENCE_VERIFICATION_SUCCESS,
    payload: { customerReferenceId, isVerified, modifiedByName }
  };
}

export interface LoadApplicationQueueCountsAction {
  type: typeof LOAD_APPLICATION_QUEUE_COUNTS;
  payload: IGetApplicationQueueCounts[];
}

export const loadApplicationQueueCounts = (queueFilterDetails: IGetApplicationQueueCounts[]): LoadApplicationQueueCountsAction => {
  return {
    type: LOAD_APPLICATION_QUEUE_COUNTS,
    payload: queueFilterDetails
  };
}

export interface LoadApplicationQueueCountsSuccessAction {
  type: typeof LOAD_APPLICATION_QUEUE_COUNTS_SUCCESS;
  payload: IApplicationQueueCountsResult[];
}

export const loadApplicationQueueCountsSuccess = (queueCounts: IApplicationQueueCountsResult[]): LoadApplicationQueueCountsSuccessAction => {
  return {
    type: LOAD_APPLICATION_QUEUE_COUNTS_SUCCESS,
    payload: queueCounts
  };
}

interface UpdateApplicationQueueFilterSuccessAction {
  type: typeof UPDATE_APPLICATION_QUEUE_FILTERS_SUCCESS;
  payload: { queueId: string, filters: State };
}

export const updateApplicationQueueFilterSuccess = (queueId: string, filters: State): UpdateApplicationQueueFilterSuccessAction => {
  return {
    type: UPDATE_APPLICATION_QUEUE_FILTERS_SUCCESS,
    payload: { queueId, filters }
  };
}

interface ClearAllApplicationQueuesFiltersSuccessAction {
  type: typeof CLEAR_ALL_APPLICATION_QUEUES_FILTERS_SUCCESS;
}

export const clearAllApplicationQueuesFiltersSuccess = (): ClearAllApplicationQueuesFiltersSuccessAction => {
  return {
    type: CLEAR_ALL_APPLICATION_QUEUES_FILTERS_SUCCESS
  };
}

export interface CreateEmployeeApplicationHistoryAction {
  type: typeof CREATE_EMPLOYEE_APPLICATION_HISTORY;
  payload: IEmployeeApplicationHistory;
}

export const createEmployeeApplicationHistory = (history: IEmployeeApplicationHistory): CreateEmployeeApplicationHistoryAction => {
  return {
    type: CREATE_EMPLOYEE_APPLICATION_HISTORY,
    payload: history
  };
}

export interface CreateEmployeeApplicationHistorySuccessAction {
  type: typeof CREATE_EMPLOYEE_APPLICATION_HISTORY_SUCCESS;
  payload: IEmployeeApplicationHistory;
}

export const createEmployeeApplicationHistorySuccess = (history: IEmployeeApplicationHistory): CreateEmployeeApplicationHistorySuccessAction => {
  return {
    type: CREATE_EMPLOYEE_APPLICATION_HISTORY_SUCCESS,
    payload: history
  };
}

export interface LoadEmployeeApplicationHistoriesAction {
  type: typeof LOAD_EMPLOYEE_APPLICATION_HISTORIES;
  payload: number;
}

export const loadEmployeeApplicationHistories = (count: number): LoadEmployeeApplicationHistoriesAction => {
  return {
    type: LOAD_EMPLOYEE_APPLICATION_HISTORIES,
    payload: count
  };
}

export interface LoadEmployeeApplicationHistoriesSuccessAction {
  type: typeof LOAD_EMPLOYEE_APPLICATION_HISTORIES_SUCCESS;
  payload: IEmployeeApplicationHistory[];
}

export const loadEmployeeApplicationHistoriesSuccess = (histories: IEmployeeApplicationHistory[]): LoadEmployeeApplicationHistoriesSuccessAction => {
  return {
    type: LOAD_EMPLOYEE_APPLICATION_HISTORIES_SUCCESS,
    payload: histories
  };
}

export interface CreateApplicationDownPaymentRequestAction {
  type: typeof CREATE_APPLICATION_DOWN_PAYMENT_REQUEST;
  payload: { applicationDownPaymentRequest: ApplicationDownPaymentRequest, paymentProcessorId: string };
}

export const createApplicationDownPaymentRequest = (applicationDownPaymentRequest: ApplicationDownPaymentRequest, paymentProcessorId: string): CreateApplicationDownPaymentRequestAction => {
  return {
    type: CREATE_APPLICATION_DOWN_PAYMENT_REQUEST,
    payload: { applicationDownPaymentRequest: applicationDownPaymentRequest, paymentProcessorId: paymentProcessorId }
  };
}

export interface CreateApplicationDownPaymentRequestSuccessAction {
  type: typeof CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_SUCCESS;
  payload: ApplicationDownPaymentRequest;
}

export const createApplicationDownPaymentRequestSuccess = (applicationDownPaymentRequest: ApplicationDownPaymentRequest): CreateApplicationDownPaymentRequestSuccessAction => {
  return {
    type: CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_SUCCESS,
    payload: applicationDownPaymentRequest
  };
}

export interface ClearNewlyCreatedDownPaymentRequestAction {
  type: typeof CLEAR_NEWLY_CREATED_DOWN_PAYMENT_REQUEST;
}

export const clearNewlyCreatedDownPaymentRequest = (): ClearNewlyCreatedDownPaymentRequestAction => {
  return {
    type: CLEAR_NEWLY_CREATED_DOWN_PAYMENT_REQUEST,
  };
}

export interface CreateApplicationDownPaymentRequestFailureAction {
  type: typeof CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_FAILURE;
  payload: string;
}

export const createApplicationDownPaymentRequestFailure = (error: string): CreateApplicationDownPaymentRequestFailureAction => {
  return {
    type: CREATE_APPLICATION_DOWN_PAYMENT_REQUEST_FAILURE,
    payload: error
  };
}

export interface UpdateKeyFactorValidationResults {
  type: typeof UPDATE_KEY_FACTOR_VALIDATION_RESULTS;
  payload: KeyFactorValidationResult[];
}

export const updateKeyFactorValidationResults = (keyFactorValidationResults: KeyFactorValidationResult[]): UpdateKeyFactorValidationResults => {
  return {
    type: UPDATE_KEY_FACTOR_VALIDATION_RESULTS,
    payload: keyFactorValidationResults
  };
}

export interface GetCarletonAprForApplicationFundingAction {
  type: typeof GET_CARLETON_APR_FOR_APPLICATION_FUNDING;
  payload: ApplicationFunding;
}

export const getCarletonAprForApplicationFunding = (applicationFunding: ApplicationFunding): GetCarletonAprForApplicationFundingAction => {
  return {
    type: GET_CARLETON_APR_FOR_APPLICATION_FUNDING,
    payload: applicationFunding
  };
}

export interface GetCarletonAprForApplicationFundingSuccessAction {
  type: typeof GET_CARLETON_APR_FOR_APPLICATION_FUNDING_SUCCESS;
  payload: ApplicationFunding;
}

export const getCarletonAprForApplicationFundingSuccess = (applicationFunding: ApplicationFunding): GetCarletonAprForApplicationFundingSuccessAction => {
  return {
    type: GET_CARLETON_APR_FOR_APPLICATION_FUNDING_SUCCESS,
    payload: applicationFunding
  };
}

// Global Search Page actions
export const SET_IS_GLOBALLY_SEARCHING = 'SET_IS_GLOBALLY_SEARCHING';
export const SET_GLOBAL_SEARCH_TEXT = 'SET_GLOBAL_SEARCH_TEXT';
export const CANCEL_GLOBAL_SEARCH = 'CANCEL_GLOBAL_SEARCH';
export const LOAD_GLOBAL_SEARCH_ITEMS = 'LOAD_GLOBAL_SEARCH_ITEMS';
export const LOAD_GLOBAL_SEARCH_ITEMS_SUCCESS = 'LOAD_GLOBAL_SEARCH_ITEMS_SUCCESS';
export const SET_LOADING_GLOBAL_SEARCH_PAGE = 'SET_LOADING_GLOBAL_SEARCH_PAGE';

export interface SetIsSearchingAction {
  type: typeof SET_IS_GLOBALLY_SEARCHING;
  payload: boolean;
}
export const setIsSearching = (isLoading: boolean): SetIsSearchingAction => {
  return {
    type: SET_IS_GLOBALLY_SEARCHING,
    payload: isLoading
  };
};

export interface LoadingGlobalSearchPageAction {
  type: typeof SET_LOADING_GLOBAL_SEARCH_PAGE;
  payload: boolean;
}
export const loadingGlobalSearchPage = (isLoading: boolean): LoadingGlobalSearchPageAction => {
  return {
    type: SET_LOADING_GLOBAL_SEARCH_PAGE,
    payload: isLoading
  };
};

export interface SetGlobalSearchTextAction {
  type: typeof SET_GLOBAL_SEARCH_TEXT;
  payload: string;
}
export const setGlobalSearchText = (searchText: string): SetGlobalSearchTextAction => {
  return {
    type: SET_GLOBAL_SEARCH_TEXT,
    payload: searchText
  };
};

export interface CancelGlobalSearchAction {
  type: typeof CANCEL_GLOBAL_SEARCH;
}
export const cancelGlobalSearch = (): CancelGlobalSearchAction => {
  return {
    type: CANCEL_GLOBAL_SEARCH
  };
};

export interface LoadGlobalSearchItemsAction {
  type: typeof LOAD_GLOBAL_SEARCH_ITEMS;
  payload: Omit<GlobalSearchDataState, "count">;
}
export const loadGlobalSearchPage = (searchData: Omit<GlobalSearchDataState, "count">): LoadGlobalSearchItemsAction => {
  return {
    type: LOAD_GLOBAL_SEARCH_ITEMS,
    payload: searchData
  };
}

export interface LoadGlobalSearchItemsSuccessAction {
  type: typeof LOAD_GLOBAL_SEARCH_ITEMS_SUCCESS;
  payload: GlobalSearchPage;
}
export const loadGlobalSearchPageSuccess = (contacts: GlobalSearchPage): LoadGlobalSearchItemsSuccessAction => {
  return {
    type: LOAD_GLOBAL_SEARCH_ITEMS_SUCCESS,
    payload: contacts
  };
}

export const SET_IS_LOADING_GLOBAL_SEARCH_PAGE = 'SET_IS_LOADING_GLOBAL_SEARCH_PAGE';
export interface SetIsLoadingGlobalSearchPageAction {
  type: typeof SET_IS_LOADING_GLOBAL_SEARCH_PAGE;
  payload: boolean;
}

export const setIsLoadingGlobalSearchPage = (isLoading: boolean): SetIsLoadingGlobalSearchPageAction => {
  return {
    type: SET_IS_LOADING_GLOBAL_SEARCH_PAGE,
    payload: isLoading
  };
};

export interface UpdateAmsCustomerNumberSuccessAction {
  type: typeof UPDATE_AMS_CUSTOMER_NUMBER_SUCCESS;
  payload: ILosCustomerIdAndAmsCustomerNumberPair[];
}
export const updateAmsCustomerNumberSuccess = (losCustomerIdAndAmsCustomerNumberPairs: ILosCustomerIdAndAmsCustomerNumberPair[]): UpdateAmsCustomerNumberSuccessAction => {
  return {
    type: UPDATE_AMS_CUSTOMER_NUMBER_SUCCESS,
    payload: losCustomerIdAndAmsCustomerNumberPairs
  };
}

export interface GetNewSignedUrlAction {
  type: typeof GET_NEW_SIGNED_URL;
  payload: { applicationId: string, customerCreditId: string };
}
export const getNewSignedUrl = (applicationId: string, customerCreditId: string): GetNewSignedUrlAction => {
  return {
    type: GET_NEW_SIGNED_URL,
    payload: { applicationId: applicationId, customerCreditId: customerCreditId }
  };
}

export interface GetNewSignedUrlSuccessAction {
  type: typeof GET_NEW_SIGNED_URL_SUCCESS;
  payload: { customerCreditId: string, signedUrl: string };
}
export const getNewSignedUrlSuccess = (customerCreditId: string, signedUrl: string): GetNewSignedUrlSuccessAction => {
  return {
    type: GET_NEW_SIGNED_URL_SUCCESS,
    payload: { customerCreditId: customerCreditId, signedUrl: signedUrl }
  };
}

export interface GetSignedUrlsAction {
  type: typeof GET_SIGNED_URLS;
  payload: { applicationId: string };
}
export const getSignedUrls = (applicationId: string): GetSignedUrlsAction => {
  return {
    type: GET_SIGNED_URLS,
    payload: { applicationId: applicationId }
  };
}

export interface GetSignedUrlsSuccessAction {
  type: typeof GET_SIGNED_URLS_SUCCESS;
  payload: { signedUrls: Record<string, string> };
}
export const getSignedUrlsSuccess = (signedUrls: Record<string, string>): GetSignedUrlsSuccessAction => {
  return {
    type: GET_SIGNED_URLS_SUCCESS,
    payload: { signedUrls: signedUrls }
  };
}

export interface VoidDownPaymentAction {
  type: typeof VOID_DOWN_PAYMENT;
  payload: { applicationDownPaymentResponseIdToVoid: string };
}
export const voidDownPayment = (applicationDownPaymentResponseIdToVoid: string): VoidDownPaymentAction => {
  return {
    type: VOID_DOWN_PAYMENT,
    payload: { applicationDownPaymentResponseIdToVoid }
  };
}

export interface VoidDownPaymentSuccessAction {
  type: typeof VOID_DOWN_PAYMENT_SUCCESS;
  payload: { voidedApplicationDownPaymentResponseId: string };
}
export const voidDownPaymentSuccess = (voidedApplicationDownPaymentResponseId: string): VoidDownPaymentSuccessAction => {
  return {
    type: VOID_DOWN_PAYMENT_SUCCESS,
    payload: { voidedApplicationDownPaymentResponseId }
  };
}

export type ApplicationActionTypes =
  | SetDealStatsAction
  | SetAppStatusesAction
  | SetDisabledAppStatusesAction
  | SetApplicationWorkflowsAction
  | SetHousingStatusesAction
  | SetMaritalStatusesAction
  | SetSaleLocationsAction
  | SetStatesAction
  | SetEmployeesAction
  | SetEditingAction
  | ClearApplicationChatsAction
  | SetAppDirtyAction
  | SetVehiclesAction
  | SetCustomerRolesAction
  | SetEmployeeRolesAction
  | SetIncomePeriodsAction
  | SetIncomeTypesAction
  | SetJobCodesAction
  | SetJobTypesAction
  | SetWorkShiftsAction
  | SetInitialLoadCompleteAction
  | UpdateEditingAppAction
  | UpdateApplicationLock
  | UpdateApplicationBudget
  | UpdateApplicationQuoteUseVehicle
  | CancelAppChangesAction
  | SetLoggedInUserAction
  | SetAmsAcvPermissionsAction
  | ScoreAppAction
  | SetQuotePaymentInfo
  | SetLoadingAction
  | SetQuotePayTypesAction
  | SetCustomFieldsAction
  | SetServerMessageAction
  | SetClearServerMessageAction
  | SetAuthenticationTimedOutAction
  | SetIpBlockedAction
  | UpdateAppVehiclePriceAction
  | AddAppVehicleAction
  | CreateNewApplicationAction
  | UpdateCreditAction
  | AddCoApplicantAction
  | AddApplicationNotesSuccessAction
  | AddApplicationChatSuccessAction
  | UpdateApplicationExceptionsSuccessAction
  | UndoApplicationExceptionsSuccessAction
  | UpdateApplicationChecklistSuccessAction
  | UpdateFormConfigurationAction
  | GetDealerTrackApplicationDataSuccessAction
  | SetDealerFlowTransactionTypeAction
  | SetApplicantCreditAppFormConfig
  | SetCoApplicantCreditAppFormConfig
  | GetDealerTrackApplicationHistorySuccessAction
  | GetApplicationChatSuccessAction
  | LoadLibrarySuccessAction
  | UpdateEmployeeAvailabilityStatusSuccessAction
  | LoadApplicationQueueData
  | UpdateAppVehicleInfoAction
  | Get700CreditTransactionIdSuccessAction
  | GetApplicationScoreSuccessAction
  | ClearApplicationScore
  | GetApplicationRehashSuccessAction
  | ClearApplicationRehashes
  | GetVehicleJdPower
  | UpdateFinanceQuotesAction
  | UpdateFinanceQuotesSuccessAction
  | SolveLeaseQuoteAction
  | SolveLeaseQuoteSuccessAction
  | SetLeaseQuoteNeedsSolveAction
  | CreateApplicationFromDealerFormSuccessAction
  | LoadJdPowersportHistoryActionSuccess
  | CreateIncomeAction
  | CreateIncomeActionSuccess
  | UpdateIncomeAction
  | UpdateIncomeActionSuccess
  | DeleteIncomeAction
  | DeleteIncomeActionSuccess
  | UpdateVehicleAction
  | UpdateVehicleActionSuccess
  | LoadEmailNotificationsActionSuccess
  | SendCustomerReferencesToAmsAction
  | SendCustomerReferencesToAmsActionSuccess
  | CreateOrganizationAction
  | CreateOrganizationSuccessAction
  | UpdateOrganizationAction
  | UpdateOrganizationSuccessAction
  | LoadManagementOrganizationsAction
  | LoadManagementOrganizationsSuccessAction
  | GetPreviousAmsLoansAction
  | GetPreviousAmsLoansSuccessAction
  | ClearPreviousAmsLoansAction
  | GetDownPaymentHistoriesAction
  | GetDownPaymentHistoriesSuccessAction
  | ClearDownPaymentHistories
  | SetInvalidAppCustomersStipulationGroupsAction
  | SetAppLoadedBusinessRulesHaveRunAction
  | LoadReleaseNotesAction
  | LoadReleaseNotesSuccessAction
  | LoadCurrentReleaseNotesAction
  | LoadCurrentReleaseNotesSuccessAction
  | SetShouldOpenGeneratePortalLinkDialogAction
  | UpdateCustomerReferenceVerification
  | UpdateCustomerReferenceVerificationSuccessAction
  | CreateCustomerReferenceAction
  | CreateCustomerReferenceSuccessAction
  | UpdateCustomerReferenceAction
  | UpdateCustomerReferenceSuccessAction
  | DeleteCustomerReferenceAction
  | DeleteCustomerReferenceSuccessAction
  | LoadApplicationQueueCountsAction
  | LoadApplicationQueueCountsSuccessAction
  | UpdateApplicationQueueFilterSuccessAction
  | CreateEmployeeApplicationHistoryAction
  | CreateEmployeeApplicationHistorySuccessAction
  | LoadEmployeeApplicationHistoriesAction
  | LoadEmployeeApplicationHistoriesSuccessAction
  | CreateApplicationDownPaymentRequestAction
  | CreateApplicationDownPaymentRequestSuccessAction
  | CreateApplicationDownPaymentRequestFailureAction
  | ClearNewlyCreatedDownPaymentRequestAction
  | UpdateKeyFactorValidationResults
  | GetCarletonAprForApplicationFundingAction
  | GetCarletonAprForApplicationFundingSuccessAction
  | SetIsSearchingAction
  | SetGlobalSearchTextAction
  | SetIsLoadingGlobalSearchPageAction
  | CancelGlobalSearchAction
  | LoadGlobalSearchItemsAction
  | LoadGlobalSearchItemsSuccessAction
  | UpdateAmsCustomerNumberSuccessAction
  | GetNewSignedUrlAction
  | GetNewSignedUrlSuccessAction
  | GetSignedUrlsAction
  | GetSignedUrlsSuccessAction
  | ClearAllApplicationQueuesFiltersSuccessAction
  | VoidDownPaymentAction
  | VoidDownPaymentSuccessAction
  | CreateCashDealApplicationAction
  | CreateCashDealApplicationSuccessAction
  | UnlockApplicationSectionsAction
  | UnlockApplicationSectionsSuccessAction
  | UnlockApplicationSectionsFailureAction
  | FetchActiveUnlockAction
  | FetchActiveUnlockSuccessAction
  | RelockApplicationAction
  | RelockApplicationSuccessAction
  | ClearActiveUnlockAction;

export type ApplicationDispatch = ThunkDispatch<AppState, unknown, ApplicationActionTypes>;
